import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  Stack, Text, PasswordInput, Button, Group, PinInput, Checkbox, CopyButton, Code, Alert, SimpleGrid, Center, Loader,
} from '@mantine/core';
import { Copy, Check, DownloadSimple, ShieldCheck, Warning } from '@phosphor-icons/react';
import { useAdminTotpSetup, useAdminTotpEnable } from '../../hooks/admin/useAdminUsers';
import { useAuthThrottle } from '../../hooks/useAuthThrottle';
import { parseApiError } from '../../utils/apiError';
import { ThrottleAlert, ErrorAlert } from './AuthFeedback';
import type { TotpSetupResponse } from '../../api/adminAuth';

function OtpQrCode({ value }: { value: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(value, { width: 200, margin: 1 })
      .then((url: string) => !cancelled && setSrc(url))
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <Center style={{ background: '#FFFFFF', borderRadius: 8, padding: 8, width: 216, height: 216 }}>
      {src ? <img src={src} width={200} height={200} alt="Authenticator app QR code" /> : <Loader size="sm" />}
    </Center>
  );
}

function downloadRecoveryCodes(codes: string[]) {
  const content = [
    'HomeHub CMS — admin 2FA recovery codes',
    `Generated: ${new Date().toISOString()}`,
    'Each code can be used only once.',
    '',
    ...codes,
  ].join('\n');
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'homehub-cms-recovery-codes.txt';
  a.click();
  URL.revokeObjectURL(url);
}

type Step = 'password' | 'scan' | 'recovery';

/**
 * Admin TOTP enrollment: password → POST totp/setup (QR) → 6-digit code → POST totp/enable
 * → the 10 recovery codes (shown only once) → user must confirm they saved them.
 */
export function TotpSetupFlow({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const setup = useAdminTotpSetup();
  const enable = useAdminTotpEnable();
  const throttle = useAuthThrottle();

  const [step, setStep] = useState<Step>('password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetup = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword) {
      setError('Please enter your current password.');
      return;
    }
    setError(null);
    setup.mutate(
      { currentPassword },
      {
        onSuccess: (data) => {
          setSetupData(data);
          setCurrentPassword('');
          setStep('scan');
        },
        onError: (err) => {
          if (!throttle.handleError(err)) setError(parseApiError(err, 'Failed to start 2FA setup').message);
        },
      },
    );
  };

  const handleEnable = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setError(null);
    enable.mutate(
      { code },
      {
        onSuccess: (data) => {
          setRecoveryCodes(data.recoveryCodes || []);
          setStep('recovery');
        },
        onError: (err) => {
          setCode('');
          setError(parseApiError(err, 'Failed to enable 2FA').message);
        },
      },
    );
  };

  if (step === 'password') {
    return (
      <form onSubmit={handleSetup}>
        <Stack gap={14}>
          <Text size="sm" c="var(--text-secondary)">
            Confirm your password to generate a new authenticator secret. You will need an app such as
            Google Authenticator or Microsoft Authenticator.
          </Text>
          <ThrottleAlert throttle={throttle} />
          <ErrorAlert message={error} />
          <PasswordInput
            label="Current Password"
            placeholder="Enter current password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            {onCancel && (
              <Button variant="subtle" onClick={onCancel} style={{ color: 'var(--text-secondary)' }}>
                Cancel
              </Button>
            )}
            <Button type="submit" color="amber" loading={setup.isPending} disabled={throttle.isLocked} fw={600}>
              Continue
            </Button>
          </Group>
        </Stack>
      </form>
    );
  }

  if (step === 'scan' && setupData) {
    return (
      <form onSubmit={handleEnable}>
        <Stack gap={14}>
          <Text size="sm" c="var(--text-secondary)">
            1. Scan this QR code with your authenticator app, or enter the secret manually.
          </Text>
          <Center>
            <OtpQrCode value={setupData.otpauthUrl} />
          </Center>
          <Group gap={8} justify="center" wrap="nowrap">
            <Code style={{ fontFamily: 'Geist Mono, monospace', wordBreak: 'break-all' }}>{setupData.secret}</Code>
            <CopyButton value={setupData.secret}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" onClick={copy} color={copied ? 'teal' : 'gray'}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Text size="sm" c="var(--text-secondary)">2. Enter the 6-digit code shown in the app.</Text>
          <ErrorAlert message={error} />
          <Center>
            <PinInput length={6} type="number" oneTimeCode value={code} onChange={setCode} />
          </Center>
          <Group justify="flex-end">
            {onCancel && (
              <Button variant="subtle" onClick={onCancel} style={{ color: 'var(--text-secondary)' }}>
                Cancel
              </Button>
            )}
            <Button type="submit" color="amber" loading={enable.isPending} disabled={code.length !== 6} fw={600}>
              Enable 2FA
            </Button>
          </Group>
        </Stack>
      </form>
    );
  }

  return (
    <Stack gap={14}>
      <Alert
        icon={<Warning size={16} />}
        color="orange"
        title="Save your recovery codes now"
        styles={{ root: { backgroundColor: 'var(--warn-dim)', borderColor: 'var(--warn-border)' } }}
      >
        <Text size="xs" c="var(--text-primary)">
          These 10 codes are shown only once. Each code can be used once to sign in if you lose access to your
          authenticator app.
        </Text>
      </Alert>
      <SimpleGrid cols={2} spacing={6}>
        {recoveryCodes.map((rc) => (
          <Code key={rc} block style={{ fontFamily: 'Geist Mono, monospace', textAlign: 'center' }}>
            {rc}
          </Code>
        ))}
      </SimpleGrid>
      <Group gap={8}>
        <CopyButton value={recoveryCodes.join('\n')}>
          {({ copied, copy }) => (
            <Button size="xs" variant="outline" color={copied ? 'teal' : 'gray'} onClick={copy}
              leftSection={copied ? <Check size={14} /> : <Copy size={14} />}>
              {copied ? 'Copied' : 'Copy all'}
            </Button>
          )}
        </CopyButton>
        <Button size="xs" variant="outline" color="gray" leftSection={<DownloadSimple size={14} />}
          onClick={() => downloadRecoveryCodes(recoveryCodes)}>
          Download .txt
        </Button>
      </Group>
      <Checkbox
        label="I have saved my recovery codes in a safe place"
        checked={savedConfirmed}
        onChange={(e) => setSavedConfirmed(e.currentTarget.checked)}
      />
      <Group justify="flex-end">
        <Button color="amber" disabled={!savedConfirmed} onClick={onDone} leftSection={<ShieldCheck size={14} />} fw={600}>
          Done
        </Button>
      </Group>
    </Stack>
  );
}
